import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowRight, AlertTriangle, FileSpreadsheet, CheckCircle2, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function ColumnMappingModal({ open, onOpenChange, preview, onConfirmed }) {
  const [mapping, setMapping] = useState({});
  const [dynLabels, setDynLabels] = useState({});
  const [match, setMatch] = useState({ status: "none" });
  const [appliedId, setAppliedId] = useState(null);
  const [pickTpl, setPickTpl] = useState("");
  const [applying, setApplying] = useState(false);
  const [saveTpl, setSaveTpl] = useState(false);
  const [saveMode, setSaveMode] = useState("new"); // new | update
  const [tplName, setTplName] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [dupHandling, setDupHandling] = useState("skip"); // skip | update | create
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!preview) return;
    setMapping({ ...(preview.mapping || {}) });
    setDynLabels({ ...(preview.dynamic_labels || {}) });
    setMatch(preview.match || { status: "none" });
    setAppliedId(preview.applied_template_id || null);
    setPickTpl("");
    setSaveTpl(false);
    setSaveMode(preview.match?.template_id ? "update" : "new");
    setTplName(preview.match?.template_name || "");
    setSourceLabel("");
    setDupHandling("skip");
  }, [preview]);

  if (!preview) return null;

  const set = (h, v) => setMapping((m) => ({ ...m, [h]: v }));
  const setLabel = (h, v) => setDynLabels((d) => ({ ...d, [h]: v }));
  const labelFor = (v) => preview.target_fields.find((f) => f.value === v)?.label || v;
  const samplesFor = (h) => (preview.samples || []).map((r) => r[h]).filter((x) => String(x ?? "").trim()).slice(0, 2);
  const newCols = new Set(match?.new_columns || []);

  const counts = {};
  Object.values(mapping).forEach((v) => { if (v && v !== "__ignore__") counts[v] = (counts[v] || 0) + 1; });
  const dups = Object.entries(counts).filter(([, c]) => c > 1).map(([v]) => labelFor(v));
  const mappedCount = Object.values(mapping).filter((v) => v && v !== "__ignore__").length;
  const formCount = preview.headers.filter((h) => !mapping[h]).length;
  const ignoreCount = Object.values(mapping).filter((v) => v === "__ignore__").length;

  const applyTemplate = async () => {
    if (!pickTpl) return;
    setApplying(true);
    try {
      const { data } = await api.post("/imports/apply-template", { preview_id: preview.preview_id, template_id: pickTpl });
      setMapping({ ...data.data.mapping });
      setDynLabels({ ...data.data.dynamic_labels });
      setMatch(data.data.match);
      setAppliedId(data.data.match.status === "exact" ? pickTpl : null);
      setSaveMode("update"); setTplName(data.data.match.template_name || "");
      toast.success(`Applied template: ${data.data.match.template_name}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplying(false); }
  };

  const confirm = async () => {
    const body = {
      preview_id: preview.preview_id, mapping, dynamic_labels: dynLabels,
      duplicate_handling: dupHandling,
      save_as_template: saveTpl, applied_template_id: appliedId,
    };
    if (saveTpl) {
      if (saveMode === "update" && match?.template_id) body.update_template_id = match.template_id;
      body.template_name = tplName.trim();
      body.source_label = sourceLabel.trim() || null;
      if (saveMode !== "update" && !body.template_name) { toast.error("Enter a template name"); return; }
    }
    setConfirming(true);
    try {
      const { data } = await api.post("/imports/confirm", body);
      const c = data.data;
      toast.success(`Imported: ${c.new_leads} new, ${c.updated_leads} updated, ${c.issues_count} issues`);
      onConfirmed?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setConfirming(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-sky bg-white sm:max-w-2xl" data-testid="column-mapping-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><FileSpreadsheet className="h-5 w-5 text-emerald-600" />Confirm column mapping</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-navy">{preview.file_name || "Uploaded file"}</span> · {preview.total_rows} rows · {preview.source_type || "CSV"}{preview.encoding && preview.encoding !== "—" ? ` · ${preview.encoding}` : ""}{preview.delimiter && preview.delimiter !== "—" ? ` · ${preview.delimiter}-separated` : ""}. Review the mapping before importing.
          </DialogDescription>
        </DialogHeader>

        {/* Match banner */}
        {match?.status === "exact" && (
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="match-banner-exact">
            <CheckCircle2 className="h-4 w-4" />Using saved mapping template: <b>{match.template_name}</b>. You can confirm directly or tweak below.
          </div>
        )}
        {match?.status === "changed_headers" && (
          <div className="space-y-1 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="match-banner-changed">
            <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />Headers changed vs template “{match.template_name}”. Please review before import.</div>
            {match.new_columns?.length > 0 && <p className="text-xs">New columns: <b>{match.new_columns.join(", ")}</b></p>}
            {match.missing_columns?.length > 0 && <p className="text-xs">Missing columns: <b>{match.missing_columns.join(", ")}</b></p>}
            {match.possible_renames?.length > 0 && <p className="text-xs">Possible renames: {match.possible_renames.map((r) => `${r.from} → ${r.to}`).join(", ")}</p>}
          </div>
        )}

        {/* Manual apply template */}
        {preview.templates?.length > 0 && (
          <div className="flex items-center gap-2">
            <select data-testid="apply-template-select" value={pickTpl} onChange={(e) => setPickTpl(e.target.value)} className="h-9 flex-1 rounded-lg border border-sky px-2 text-sm">
              <option value="">Apply a saved template…</option>
              {preview.templates.map((t) => <option key={t.id} value={t.id}>{`${t.template_name} (${t.source_type})`}</option>)}
            </select>
            <button onClick={applyTemplate} disabled={!pickTpl || applying} data-testid="apply-template-button" className="rounded-lg border border-sky px-3 py-2 text-xs font-semibold text-loop hover:bg-sky/30 disabled:opacity-50">{applying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700" data-testid="map-summary-mapped">{mappedCount} mapped</span>
          <span className="rounded-full bg-sky/60 px-2.5 py-1 font-semibold text-navy">{formCount} form answers</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-500">{ignoreCount} ignored</span>
        </div>

        {dups.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="map-dup-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>Multiple columns map to: <b>{dups.join(", ")}</b>. The last one will win.</span>
          </div>
        )}

        {/* Mapping rows */}
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr,1fr] gap-3 border-b border-sky px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink/40">
            <span>CSV column / sample</span><span>Maps to</span>
          </div>
          {preview.headers.map((h) => {
            const samples = samplesFor(h);
            const isForm = !mapping[h];
            return (
              <div key={h} data-testid={`map-row-${h}`} className="grid grid-cols-[1fr,1fr] items-start gap-3 rounded-lg px-1 py-1.5 hover:bg-mist/40">
                <div className="min-w-0 pt-1.5">
                  <p className="flex items-center gap-1.5 truncate text-sm font-medium text-navy">
                    {h}{newCols.has(h) && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">NEW</span>}
                  </p>
                  {samples.length > 0 && <p className="truncate text-[11px] text-ink/45">e.g. {samples.join(" · ")}</p>}
                </div>
                <div className="space-y-1">
                  <select
                    data-testid={`map-select-${h}`}
                    value={mapping[h] ?? ""}
                    onChange={(e) => set(h, e.target.value)}
                    className={`h-9 w-full rounded-lg border px-2 text-sm outline-none focus:border-loop ${mapping[h] === "__ignore__" ? "border-slate-200 bg-slate-50 text-slate-400" : "border-sky bg-white"}`}
                  >
                    <option value="">↳ Store as form answer</option>
                    {preview.target_fields.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    <option value="__ignore__">Ignore this column</option>
                  </select>
                  {isForm && (
                    <input data-testid={`map-label-${h}`} value={dynLabels[h] ?? ""} onChange={(e) => setLabel(h, e.target.value)} placeholder={`Question label (default: ${h})`} className="h-8 w-full rounded-lg border border-sky/70 bg-mist/30 px-2 text-xs outline-none focus:border-loop" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Duplicate handling */}
        <div className="rounded-xl border border-sky/70 bg-mist/30 p-3" data-testid="dup-handling-block">
            <p className="mb-2 text-sm font-medium text-navy">If a lead already exists (matched by Lead ID, email or phone)</p>
            <div className="flex flex-col gap-1.5">
              {[
                ["skip", "Skip duplicates", "Keep the existing lead untouched (recommended)."],
                ["update", "Update existing", "Refresh source fields (name, campaign, ad…); never touches stage, owner, notes."],
                ["create", "Create anyway", "Always add a new lead, even if a match exists."],
              ].map(([val, label, desc]) => (
                <label key={val} className="flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-white">
                  <input type="radio" name="dupHandling" className="mt-0.5" data-testid={`dup-${val}`} checked={dupHandling === val} onChange={() => setDupHandling(val)} />
                  <span className="text-sm"><span className="font-semibold text-navy">{label}</span><span className="ml-1.5 text-xs text-ink/50">{desc}</span></span>
                </label>
              ))}
            </div>
        </div>

        {/* Save as template */}
        <div className="rounded-xl border border-sky/70 bg-mist/30 p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-navy">
            <input type="checkbox" data-testid="save-template-checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} />
            <Sparkles className="h-4 w-4 text-loop" />Save this mapping as a template
          </label>
          {saveTpl && (
            <div className="mt-3 space-y-2">
              {match?.template_id && (
                <div className="flex flex-wrap gap-3 text-xs">
                  <label className="flex items-center gap-1.5"><input type="radio" name="saveMode" checked={saveMode === "update"} onChange={() => setSaveMode("update")} data-testid="save-mode-update" />Update “{match.template_name}”</label>
                  <label className="flex items-center gap-1.5"><input type="radio" name="saveMode" checked={saveMode === "new"} onChange={() => setSaveMode("new")} data-testid="save-mode-new" />Save as new</label>
                </div>
              )}
              <input data-testid="template-name-input" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name (e.g. Meta Lead Ads)" className="h-9 w-full rounded-lg border border-sky px-2.5 text-sm outline-none focus:border-loop" />
              <input data-testid="template-label-input" value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} placeholder="Source label (optional, e.g. Client X)" className="h-9 w-full rounded-lg border border-sky px-2.5 text-sm outline-none focus:border-loop" />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={() => onOpenChange(false)} className="rounded-lg border border-sky px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-mist">Cancel</button>
          <button onClick={confirm} disabled={confirming} data-testid="confirm-import-button" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
            {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Import {preview.total_rows} rows<ArrowRight className="h-4 w-4" /></>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
